/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

// Utils
import { cn } from '@/lib/utils';

// Components
import { Button } from '@/components/UI/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/UI/Popover';
import { Label } from '../../UI/Label';
import { DatePickerCalendar } from './DatePickerCalendar';
import { TimePicker } from '@/components/TimePicker/TimePicker';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
type DatePickerProps = {
  date?: Date;
  setDate: (date: Date | undefined) => void;
  label?: string;
  error?: FieldError;
  register?: UseFormRegisterReturn;
  disabled?: boolean;
  icon?: React.ReactNode;
  maxDate?: Date;
  fromDate?: Date;
  showTime?: boolean;
  className?: string;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  date,
  setDate,
  label,
  error,
  register,
  disabled,
  icon,
  maxDate,
  fromDate,
  showTime = false,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation();

  return (
    <div className={cn(disabled ? 'cursor-not-allowed' : '')}>
      <Label className="text-bg-blue font-semibold text-background-1 flex items-center">
        {icon && <div className="mr-2 mb-1">{icon}</div>}
        {label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className="overflow-hidden text-ellipsis">
          <Button
            variant="ghost"
            type="button"
            className={cn(
              'w-full justify-between h-[53px] rounded-t-sm focus-visible:outline-none bg-white rounded-b-none hover:border-b-2 hover:border-gray-400',
              className,
              {
                'border-b-2 border-destructive': error,
              },
              disabled ? 'opacity-50' : ''
            )}
            disabled={disabled}
            aria-expanded={open}
          >
            {date ? (
              showTime ? (
                format(date, 'PPPP à HH:mm', { locale: fr })
              ) : (
                format(date, 'PPPP', { locale: fr })
              )
            ) : (
              <span>{t('global.pick_date')}</span>
            )}
            <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" sideOffset={-150}>
          <div className="space-y-4">
            <DatePickerCalendar
              mode="single"
              selected={date}
              onSelect={(selectedDate) => {
                if (!maxDate || (selectedDate && selectedDate <= maxDate)) {
                  setDate(selectedDate);
                }
              }}
              initialFocus
              locale={fr}
              toDate={maxDate}
              fromDate={fromDate}
              captionLayout="dropdown-buttons"
              fromYear={1960}
            />

            {showTime && (
              <div className="flex justify-center">
                <TimePicker
                  date={date}
                  setDate={(selectedTime) => {
                    if (date && selectedTime) {
                      const updatedDate = new Date(date);
                      updatedDate.setHours(selectedTime.getHours());
                      updatedDate.setMinutes(selectedTime.getMinutes());

                      setDate(updatedDate);
                    } else {
                      setDate(selectedTime);
                    }
                  }}
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <input
        type="hidden"
        value={date ? format(date, 'yyyy-MM-dd') : ''}
        {...register}
        aria-invalid={!!error}
      />
      {error && (
        <p className="ml-2 mt-1 text-xs text-destructive">
          {t(error.message || '')}
        </p>
      )}
    </div>
  );
};
